import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { UserPlus } from "lucide-react";
import GoogleButton, { OrDivider } from "@/components/auth/GoogleButton";
import VerifyEmailNotice from "@/components/auth/VerifyEmailNotice";

export default function Register() {
  const { register, loginWithGoogle } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", company_name: "", email: "", password: "" });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [sentTo, setSentTo] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    const r = await register(form);
    setLoading(false);
    if (r.verificationSent) setSentTo(r.email);
    else setErr(r.error);
  };

  const google = async () => {
    setErr(""); setLoading(true);
    const r = await loginWithGoogle();
    setLoading(false);
    if (r.ok) nav("/dashboard");
    else if (r.mfaResolver) nav("/login");
    else setErr(r.error);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-6 py-12">
      <div className="w-full max-w-md card-flat p-8" data-testid="register-card">
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-6">
            <div className="w-8 h-8 bg-brand text-white grid place-items-center font-display font-bold rounded-sm">T</div>
            <div className="font-display font-bold tracking-tight">TWIN</div>
          </div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Create account</h1>
          <p className="text-ink-muted text-sm mt-2">
            You'll join as a <span className="font-medium text-ink">Team Member</span>. Your admin will assign your role and permissions.
          </p>
        </div>

        {sentTo ? (
          <div className="space-y-5">
            <VerifyEmailNotice email={sentTo} />
            <Link to="/login" data-testid="register-go-login-btn" className="w-full bg-brand hover:bg-brand-hover text-white font-medium py-2.5 rounded-sm text-sm flex items-center justify-center">
              Go to sign in
            </Link>
          </div>
        ) : (<>
        <GoogleButton onClick={google} loading={loading} label="Sign up with Google" testid="google-signup-btn" />
        <OrDivider />
        <form onSubmit={submit} className="space-y-4" data-testid="register-form">
          {[
            { k: "name", label: "Full name", type: "text" },
            { k: "company_name", label: "Company name", type: "text", hint: "Used to find your row when uploading tender result sheets" },
            { k: "email", label: "Work email", type: "email" },
            { k: "password", label: "Password (min 6 chars)", type: "password" },
          ].map(({ k, label, type, hint }) => (
            <div key={k}>
              <label className="stat-label block mb-1.5">{label}</label>
              <input
                type={type}
                value={form[k]}
                onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))}
                data-testid={`register-${k}-input`}
                className="w-full px-3 py-2.5 border border-ink-line bg-white rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand"
                required
                minLength={k === "password" ? 6 : 1}
              />
              {hint && <div className="text-[11px] text-ink-muted mt-1">{hint}</div>}
            </div>
          ))}

          {err && <div className="text-sm text-warn bg-warn-soft border border-warn/30 px-3 py-2 rounded-sm" data-testid="register-error">{err}</div>}

          <button
            type="submit"
            disabled={loading}
            data-testid="register-submit-btn"
            className="w-full bg-brand hover:bg-brand-hover disabled:opacity-60 text-white font-medium py-2.5 rounded-sm text-sm transition-colors flex items-center justify-center gap-2"
          >
            <UserPlus className="w-4 h-4" /> {loading ? "Creating…" : "Create account"}
          </button>
        </form>
        </>)}


        <div className="mt-6 pt-6 border-t border-ink-line text-sm text-ink-muted">
          Already have an account?{" "}
          <Link to="/login" className="text-brand hover:underline font-medium" data-testid="login-link">
            Sign in
          </Link>
        </div>

        <div className="mt-4 text-[11px] uppercase tracking-wider text-ink-muted">
          Need admin access? Ask your TWIN administrator to assign a role.
        </div>
      </div>
    </div>
  );
}
